import { useI18n } from "@baton/i18n";
import { countryName } from "./countryName.ts";

/**
 * A country as its flag (2026-09-20, by the owner's decision: a flag reads faster than a
 * code). The flag is the two regional indicator characters the code maps to, which every
 * platform but Windows draws as a flag; Windows draws the two letters, which is the code, so
 * the fallback is itself readable. The country's name is the accessible label and the title,
 * so a screen reader and a hover both say the country, never the code.
 */
export interface CountryFlagProps {
  /** ISO 3166-1 alpha-2, upper case. */
  readonly code: string;
  /** The name beside the flag, for a surface that names the country in words too. */
  readonly withName?: boolean;
  readonly className?: string;
}

const REGIONAL_INDICATOR_BASE = 0x1f1e6;

/** The flag characters of an alpha-2 code: `PT` becomes the two regional indicators for P and T. */
export function flagOf(code: string): string {
  const upper = code.toUpperCase();
  return String.fromCodePoint(REGIONAL_INDICATOR_BASE + upper.charCodeAt(0) - 65, REGIONAL_INDICATOR_BASE + upper.charCodeAt(1) - 65);
}

export function CountryFlag({ code, withName = false, className }: CountryFlagProps) {
  const { locale } = useI18n();
  const name = countryName(locale, code);
  return (
    <span className={className} title={withName ? undefined : name}>
      <span className="b-country-flag" aria-hidden="true">
        {flagOf(code)}
      </span>
      {withName ? <span className="b-country-flag__name">{name}</span> : <span className="sr-only">{name}</span>}
    </span>
  );
}
